/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { fromEvent, Subject } from 'rxjs';
import { AsperaDrag } from '../../components/AsperaDrag';
import { SubManager } from '../../classes/SubManager';
import { altKeysUsed } from '../../helpers/starterHelpers';
import { AsperaError } from '../../classes/AsperaError';
import { getEnvironmentVar } from '../../helpers/helpers';
/**
 * Service to handle dragging items around
 */
export class DragDropService {
    constructor() {
        /** Items being dragged */
        this.itemsDragged = [];
        /** Indicate if service is setup and has watcher on body */
        this.setup = false;
        /** Indicate if actively dragging */
        this.dragging = false;
        /** Current dragover item or `tableId` or undefined */
        this.currentDragOverItem = undefined;
        /** Subject for resetting drag across tables */
        this.triggerStateUpdate = new Subject();
        /** Subject for dragging state (rendering drag box) */
        this.dragStateUpdate = new Subject();
        /** Subscription manager */
        this.subManager = new SubManager();
        /** Mouse event counter */
        this.mouseEventCounter = 0;
        this.onDragOver = (event) => {
            event.stopPropagation();
            event.preventDefault();
            if (this.dragState && this.itemsDragged.length) {
                this.dragState.alt = altKeysUsed(event);
                this.dragState.xCoordinate = event.clientX;
                this.dragState.yCoordinate = event.clientY;
                this.dragState.visible = true;
                this.dragStateUpdate.next(this.dragState);
            }
        };
        /** Firefox triggers MouseEvent about three times when you start dragging. Not sure why. Seems like bug as no other browser triggers MouseMove and Drag at same time */
        this.onMouseMove = () => {
            if (this.mouseMoveTimer) {
                clearTimeout(this.mouseMoveTimer);
            }
            if (this.dragging || this.dragOverItem) {
                this.mouseEventCounter++;
                if (this.mouseEventCounter > 20) {
                    this.mouseMoveTimer = setTimeout(() => {
                        this.mouseEventCounter = 0;
                        this.triggerResetDrag();
                    }, 500);
                }
            }
        };
    }
    get mainView() {
        return React.createElement(AsperaDrag, null);
    }
    /** Observable to subscribe to when triggered update state */
    get triggerState() {
        return this.triggerStateUpdate.asObservable();
    }
    /** Observable to subscribe to when drag state is updated */
    get triggerDragState() {
        return this.dragStateUpdate.asObservable();
    }
    /** Current dragover item or `tableId` or undefined */
    get dragOverItem() {
        return this.currentDragOverItem;
    }
    /** Current dragover item or `tableId` or undefined */
    set dragOverItem(item) {
        this.currentDragOverItem = item;
        this.triggerStateUpdate.next(true);
    }
    get safeCurrentItemsDragged() {
        const newList = [];
        this.itemsDragged.forEach(item => {
            newList.push(item);
        });
        return newList;
    }
    setDragRules(dragState) {
        this.dragState = dragState;
    }
    triggerResetDrag() {
        this.dragging = false;
        this.itemsDragged = [];
        this.tableIdDragging = undefined;
        this.dragOverItemTableId = undefined;
        this.currentDragOverItem = undefined;
        this.triggerStateUpdate.next(true);
        this.dragStateUpdate.next({ visible: false, mainText: '', allowed: false, errorDescription: '', errorMessage: '', defaultBehavior: undefined });
    }
    /** Start the Drag and Drop Service and get the view item for rendering. Should be done in AsperaAppWrapper */
    renderView() {
        if (!this.setup) {
            this.setup = true;
            this.subManager.setSubscription(fromEvent(window, getEnvironmentVar('use_mouse_for_drag') ? 'mouseover' : 'dragover').subscribe({
                next: this.onDragOver,
                error: error => {
                    new AsperaError('DragDropService: unable to subscribe drag event on window', error);
                },
            }));
            this.subManager.setSubscription(fromEvent(window, 'mousemove').subscribe({
                next: this.onMouseMove,
                error: error => {
                    new AsperaError('DragDropService: unable to subscribe mouse move event on window', error);
                },
            }));
        }
        return this.mainView;
    }
}
/** The shared Aspera Drag Drop Service */
export const dragging = new DragDropService();
